import { useInventory, useStoreInventories } from '@gozoki/api';
import { Stack, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';

import BlockerLoader from '../../components/loaders/BlockerLoader';
import { useInventoryProductRows } from '../../utils/hooks/useInventoryProductRows';
import { InventoryDetails } from './InventoryDetails';
import { InventoryGrid } from './InventoryGrid';
import { InventoryStateDetails } from './InventoryStateDetails';
import { InventoryValidateOrCancel } from './InventoryValidateOrCancel';

interface PendingInventoryTabProps {
    shopId: number;
    setTab: (tab: number) => void;
}

export const PendingInventoryTab = ({ shopId, setTab }: PendingInventoryTabProps) => {
    const { data: inventories, isLoading: isLoadingInventories } = useStoreInventories(shopId);

    // const [hasShownDeltaWarning, setHasShownDeltaWarning] = useState<boolean>(false);
    const [isWaitingForInventoryFinish, setIsWaitingForInventoryFinish] = useState<boolean>(false);

    // Find the first pending inventory
    const pendingInventory = useMemo(() => {
        if (!inventories) return undefined;
        return inventories.find((inv) => inv.status === 'pending');
    }, [inventories]);

    const {
        data: inventoryDetail,
        isPending: isLoading,
        refetch: refetchInventory,
    } = useInventory(pendingInventory?.id ?? 0);

    // Reset waiting state when inventory is finished
    useEffect(() => {
        if (isWaitingForInventoryFinish && inventoryDetail?.isFinished) {
            setIsWaitingForInventoryFinish(false);
        }
    }, [isWaitingForInventoryFinish, inventoryDetail?.isFinished]);

    const productRows = useInventoryProductRows(inventoryDetail);

    if (isLoadingInventories || !pendingInventory || !inventoryDetail || isLoading) {
        let message = '';
        if (isLoadingInventories) {
            message = "Recherche d'un inventaire en cours";
        } else if (!pendingInventory) {
            message = 'Aucun inventaire en cours';
        } else if (!inventoryDetail) {
            message = 'Inventaire trouvé, chargement';
        } else if (isLoading) {
            message = 'Chargement...';
        }

        return (
            <Typography sx={{ textAlign: 'center', paddingY: '20px' }} variant="h6">
                {message}
            </Typography>
        );
    }

    return (
        <div style={{ flex: 1, position: 'relative' }}>
            <Stack direction="column" spacing={2}>
                <InventoryStateDetails pendingInventory={pendingInventory} shopId={shopId} />
                <InventoryValidateOrCancel
                    inventoryId={pendingInventory.id}
                    shopId={shopId}
                    setTab={setTab}
                />
                <InventoryDetails
                    label={inventoryDetail.label || ''}
                    comment={inventoryDetail.comment || ''}
                    inventoryId={inventoryDetail.id}
                    onValidate={() => refetchInventory()}
                />
                <InventoryGrid isLoading={isLoading} productRows={productRows} />
            </Stack>

            <BlockerLoader isLoading={isLoading} />
        </div>
    );
};
